import {
  setCookie,
  getCookie,
  isVersionBefore,
  dataAttributes,
  getFixVersion,
  getUnaffectedVersion,
  getInfoFromQueryParams,
  parseQuery,
} from './utils.js';

window.iframeLoaded = false;

const HIDDEN = 'hidden';

const registerAlerts = () => {
  const alerts = document.querySelectorAll(`.alert:not(.${HIDDEN})`);
  alerts.forEach((alert) => {
    const alertClose = alert.querySelector('.alert__close');

    alertClose?.addEventListener('click', () => {
      alert.classList.add(HIDDEN);
      setCookie(alert.id, false);
      checkAndHideNotificationsSection();
    });
  });
};

const getElementsWithFixVersion = () => {
  const nodes = document.querySelectorAll(
    `[${dataAttributes.fixVersion}], [${dataAttributes.unaffectedVersion}]`
  );
  return Array.from(nodes);
};

/* Hides all the elements except for Alert messages that have the attribute data-takeover="true" */
const hideElements = () => {
  /* Selects all the elements except for the children of that header that have data-takeover attribute and no-takeover class  */
  const elements = document.querySelectorAll(
    `.main > *, .header > *:not([${dataAttributes.takeOver}]):not(.no-takeover)`
  );

  elements.forEach((element) => {
    element.classList.add(HIDDEN);
  });
};

/*
  Looks for the elements with unaffected version and fix version.
  Then, check if the elements should be or not displayed based on the Nexus version.
 */
const showElements = () => {
  const info = getInfoFromQueryParams();
  const nodes = getElementsWithFixVersion();

  const filteredNodes = nodes.filter((node) => {
    const minVersion = getUnaffectedVersion(node);
    const maxVersion = getFixVersion(node);
    const inBetween =
      isVersionBefore(minVersion, info?.version) &&
      isVersionBefore(info?.version, maxVersion);

    // Show alert only when the instance DB matches the alert's declared type.
    // Alerts without data-database-type are shown regardless of DB.
    // When databaseType param is absent (old Nexus), fall through to version gate.
    const requiredDb = node.getAttribute(dataAttributes.databaseType)?.toLowerCase();
    const actualDb = info?.databaseType?.toLowerCase();
    const dbAllowed =
      !requiredDb ||   // no restriction on this alert
      !actualDb ||     // old Nexus without param — fall back to version gate
      actualDb === requiredDb;

    /* If the Node fixed version is in between and the database type allows it, removes the hidden class */
    if (inBetween && dbAllowed) {
      /* Checks if there is a cookie to keep the element hidden */
      const checkCookie = getCookie(node.id) ?? true;

      if (checkCookie) {
        node.classList.remove(HIDDEN);
      }
    }

    return inBetween && dbAllowed;
  });

  /* Affected version usually is a version with critical issues */
  const affectedVersion = filteredNodes.some((node) =>
    node.getAttribute(dataAttributes.takeOver)
  );

  if (affectedVersion) {
    hideElements();
  }
};

/* toggle the icon in accordion section of supported formats and types */
function toggleIcon() {
  const trigger = document.querySelector('.accordion-header');
  trigger.addEventListener('click', () => {
    const icon = document.querySelector('.accordion-header > i');
    const isOpen = document.querySelector('.formats-accordion').hasAttribute('open');
    isOpen ? icon.classList.replace('fa-circle-chevron-up', 'fa-circle-chevron-down') : icon.classList.replace('fa-circle-chevron-down', 'fa-circle-chevron-up');
  });
}

/* Check if there is an event listener for the show more, if not add one with toggleShowMore */
let showMoreListenerAdded = false;
function handleShowMoreClick() {
  const alerts = document.querySelectorAll(`.alert:not(.${HIDDEN}):not([data-takeover="true"])`);
  const showMore = document.querySelector('.show-more-summary');

  if (alerts.length <= 1) {
    showMore.style.display = 'none';
  } else {
    showMore.style.display = '';
    showMore.style.pointerEvents = 'auto';
    showMore.style.opacity = '1';

    if (!showMoreListenerAdded) {
      showMore.addEventListener('click', toggleShowMore);
      showMoreListenerAdded = true;
    }
  }
}

/* Toggle between show more and show less */
function toggleShowMore() {
  const showMore = document.querySelector('.show-more-summary');
  const showMoreText = showMore.querySelector('.accordion-show-more');
  const showMoreIcon = showMore.querySelector('i');

  if (showMoreText.textContent === 'Show More') {
    showMoreText.textContent = 'Show Less';
    showMoreIcon.classList.replace('fa-caret-down', 'fa-caret-up');
  } else if (showMoreText.textContent === 'Show Less') {
    showMoreText.textContent = 'Show More';
    showMoreIcon.classList.replace('fa-caret-up', 'fa-caret-down');
  }
}

/* Check if there are any alerts that have a cookie to be hidden */
function checkAlertCookies() {
  const alerts = document.querySelectorAll(`.alert:not(.${HIDDEN}):not([data-takeover="true"])`);
  for (alert of alerts) {
    const checkCookie = getCookie(alert.id) ?? true;
    if(!checkCookie) {
      alert.classList.add(HIDDEN);
    }
  }
}

/* Check if there are any alerts and hide the notifications section if there are none */
function checkAndHideNotificationsSection() {
  checkAlertCookies();
  const alerts = document.querySelectorAll(`.alert:not(.${HIDDEN}):not([data-takeover="true"])`);
  const notificationsSection = document.querySelector('.notifications-section');

  if (alerts.length !== 0 && notificationsSection) {
    const notificationText = alerts.length === 1 ? 'notification' : 'notifications';
    const text = `You have <strong>${alerts.length} ${notificationText}</strong> to review`;
    const statusIndicator = document.querySelector('.nx-status-indicator');
    const content = document.querySelector('.notifications-accordion-content');
    const header = document.querySelector('.notifications-accordion-header');

    statusIndicator.innerHTML = text;
    // Move the first alert to the header section of the notifications accordion
    header.appendChild(alerts[0]);

    // Move the alerts to the content section of the notifications accordion
    for (let i = 1; i < alerts.length; i++) {
      content.appendChild(alerts[i]);
    };

    handleShowMoreClick();
  }
  else {
    notificationsSection.style.display = 'none';
  }
}

/* Initialize the survey iframe with dynamic parameters from query string */
const initializeSurvey = () => {
  const surveyContainer = document.getElementById('survey-iframe-container');
  if (!surveyContainer) return;

  const params = parseQuery();
  
  // Get version and edition from query params (no fallbacks - these should always be present)
  const version = params.version;
  const edition = params.edition;

  // Don't render survey if required parameters are missing
  if (!version || !edition) {
    return;
  }

  // Construct the Typeform URL with parameters
  const surveyUrl = `https://sonatype.typeform.com/to/BNKFZEbz#version=${encodeURIComponent(version)}&edition=${encodeURIComponent(edition)}`;

  // Create and inject the iframe
  const iframe = document.createElement('iframe');
  iframe.className = 'survey';
  iframe.src = surveyUrl;
  iframe.frameBorder = '0';
  iframe.textContent = 'Loading...';

  // Replace the loading text with the iframe
  surveyContainer.innerHTML = '';
  surveyContainer.appendChild(iframe);
};


function init() {
  showElements();
  registerAlerts();
  toggleIcon();
  checkAndHideNotificationsSection();
  initializeSurvey();

  /*
   Body is display none by default, once all the scripts are executed shows the body with the corresponding elements.
  */
  document.body.style = '';
  // Marks iframe as loaded so that nexus can display it.
  window.iframeLoaded = true;
}

window.addEventListener('load', init);

